/**
 * Aurora analytics — thin abstraction over Plausible AND/OR PostHog.
 *
 * Either, both, or neither can be configured via env vars:
 *   REACT_APP_PLAUSIBLE_DOMAIN   (e.g. "withaurora.love")
 *   REACT_APP_PLAUSIBLE_SRC      (optional override, default https://plausible.io/js/script.outbound-links.js)
 *   REACT_APP_POSTHOG_KEY        (PostHog project API key)
 *   REACT_APP_POSTHOG_HOST       (optional, default https://us.i.posthog.com)
 *
 * When nothing is configured the module silently no-ops — safe to leave in
 * development / preview builds without leaking events.
 */

const w = typeof window !== "undefined" ? window : null;

const config = {
  plausibleDomain: process.env.REACT_APP_PLAUSIBLE_DOMAIN || "",
  plausibleSiteId: process.env.REACT_APP_PLAUSIBLE_SITE_ID || "",
  plausibleSrc:
    process.env.REACT_APP_PLAUSIBLE_SRC ||
    "https://plausible.io/js/script.outbound-links.tagged-events.js",
  posthogKey: process.env.REACT_APP_POSTHOG_KEY || "",
  posthogHost: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
};

let initialized = false;
const hasPlausible = () => Boolean(config.plausibleDomain || config.plausibleSiteId);
const hasPosthog = () => Boolean(config.posthogKey);

const sendPlausibleEvent = (event, props) => {
  if (!hasPlausible() || typeof w?.plausible !== "function") return;
  w.plausible(event, props ? { props } : undefined);
};

const sendPosthogEvent = (event, props) => {
  if (!hasPosthog() || !w?.posthog || typeof w.posthog.capture !== "function") return;
  w.posthog.capture(event, props);
};

const getCurrentLang = () =>
  w?.localStorage?.getItem("aurora_lang") ||
  w?.document?.documentElement?.lang ||
  "en";

const loadPlausible = () => {
  if (!w) return;
  if (!config.plausibleDomain && !config.plausibleSiteId) return;
  if (document.querySelector("script[data-aurora-plausible]")) return;
  const s = document.createElement("script");
  s.defer = true;
  s.src = config.plausibleSrc;
  if (config.plausibleDomain) s.setAttribute("data-domain", config.plausibleDomain);
  if (config.plausibleSiteId) s.setAttribute("data-site-id", config.plausibleSiteId);
  s.setAttribute("data-aurora-plausible", "1");
  document.head.appendChild(s);

  // Provide a queued plausible() in case events fire before the script loads.
  if (!w.plausible) {
    w.plausible = function (...args) {
      (w.plausible.q = w.plausible.q || []).push(args);
    };
  }
};

const loadPostHog = () => {
  if (!w || !config.posthogKey) return;
  if (w.posthog && w.posthog.__loaded) return;
  // Minimal PostHog snippet (queues calls until the lib loads).
  /* eslint-disable */
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split(".");
          2 == o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement("script")).type = "text/javascript"),
          (p.async = !0),
          (p.src = s.api_host + "/static/array.js"),
          (r = t.getElementsByTagName("script")[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = "posthog"),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = "posthog";
              return "posthog" !== a && (e += "." + a), t || (e += " (stub)"), e;
            },
            u.people.toString = function () {
              return u.toString(1) + ".people (stub)";
            },
            o =
              "capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys getNextSurveyStep onSessionId".split(
                " "
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, w.posthog || []);
  w.posthog.init(config.posthogKey, {
    api_host: config.posthogHost,
    capture_pageview: false, // we'll send manually via trackPageview()
    persistence: "memory", // privacy-friendly: no cookies
  });
  /* eslint-enable */
};

export const initAnalytics = () => {
  if (initialized || !w) return;
  initialized = true;
  loadPlausible();
  loadPostHog();
};

/** Fire a custom event to whichever providers are configured. */
export const track = (event, props = {}) => {
  if (!w) return;
  const payload = {
    ...props,
    path: w.location?.pathname,
    lang: getCurrentLang(),
  };
  try {
    sendPlausibleEvent(event, payload);
    sendPosthogEvent(event, payload);
  } catch (_) {
    // Never let analytics break the UI.
  }
};

/** Manually capture a virtual pageview (SPA route change). */
export const trackPageview = () => {
  if (!w) return;
  try {
    sendPlausibleEvent("pageview");
    sendPosthogEvent("$pageview", { $current_url: w.location?.href });
  } catch (_) {
    // Silent fail.
  }
};

export const analyticsConfigured = () =>
  Boolean(config.plausibleDomain || config.plausibleSiteId || config.posthogKey);
